/**
 * #4771: the one-time cleanup that deletes the IndexedDB databases iOS left behind when the
 * app moved its local storage to SQLite.
 *
 * `indexedDB.deleteDatabase(name)` deletes nothing by the time it returns. It hands back a
 * request, and the answer arrives later as one of three events: `success` (the database is
 * gone), `error` (it is not) or `blocked` (another connection still has it open, after which
 * nothing further may ever be fired). The cleanup used to read none of them, and then wrote
 * its "finished" marker on the very next line — so a deletion that failed was both invisible
 * and permanent, because that marker makes every later boot return immediately.
 *
 * It lives in its own file, free of Angular, so the behaviour can be tested directly:
 * `app.component.ts` sits on a component with a great many injected services and cannot be
 * driven from a unit test. This is the same split `signout-teardown.ts` already uses, for the
 * same reason. `legacy-idb-cleanup.test.ts` also carries a source tripwire that fails if the
 * component ever goes back to deleting databases itself.
 *
 * The deleting itself, and the bounded wait for the answers, are NOT here: they are shared with
 * the sign-out wipe in `idb-database-deletion.ts` (`df-c5943ac55882`). What is here is this
 * cleanup's own policy — which databases it looks at, how it reads the outcomes, when it may
 * retry, and when it gives up.
 *
 * Two things here are bounded, and both bounds are load-bearing:
 *
 *  - The WAIT for the answers, because a blocked deletion can fire no event at all and an
 *    unbounded wait would never end. That one lives in the shared module.
 *  - The RETRYING, because a device where the deletion can never succeed would otherwise
 *    re-run and re-report on every single boot for ever, and a report that fires constantly
 *    buries the ones that matter. Stopping costs only wasted storage on that device: the
 *    leftovers are dead data the app no longer reads, no user data is lost, and nobody loses
 *    access.
 *
 * #4834: none of that could reach the devices it was built for until the marker key was
 * versioned, because the ORIGINAL cleanup marked every device finished whether or not its
 * deletions worked. `SQLITE_MIGRATION_DONE_KEY` below carries that story, including the two
 * things that had to be settled before re-running a deletion on real devices.
 */

import { errorReportFields } from '../services/error-patterns';
import {
  deleteIdbDatabasesAsync,
  IdbDeletionOutcome,
  IdbDeletionResult,
  IdbForDeletion,
} from './idb-database-deletion';

/** Databases named with this prefix are the ones the old storage engine left behind. */
const LEGACY_DB_NAME_PREFIX = '_pouch_';

/**
 * The part of a name that marks a PouchDB VIEW INDEX rather than a real database.
 *
 * PouchDB keeps each map/reduce view's index in its own IndexedDB database named
 * `_pouch_<parent>-mrview-<hash>`. Those are derived data: throw one away and PouchDB
 * rebuilds it from the documents on the next query.
 */
const VIEW_INDEX_NAME_MARKER = '-mrview-';

/** How much of the origin's IndexedDB the caller wants named. */
export interface ListPouchIndexedDbNamesOptions {
  /**
   * Leave out the `-mrview-` view-index databases.
   *
   * The two callers genuinely want different answers, which is why this is an option
   * rather than a fixed rule. **This cleanup wants them INCLUDED** — it is deleting
   * everything the old engine left behind, and a view index left behind is still
   * wasted storage. **#4981 phase 3's migration wants them EXCLUDED** — copying a
   * view index would spend the migration's whole time budget duplicating data that
   * rebuilds itself, and the copy would be stale the moment a document changed.
   */
  excludeViewIndexes: boolean;
}

/**
 * Name every PouchDB database in this origin's IndexedDB. (#4981 phase 3)
 *
 * Exported because two callers now need it and a second copy would drift: this
 * file's own cleanup, which DELETES what it finds, and phase 3's migration, which
 * READS what it finds. Those are opposite operations deliberately kept in separate
 * modules — see `design-docs/android-local-database-engine.md`, "Which databases move,
 * and which are skipped" — but they must agree about what is on the device.
 *
 * Throws whatever `databases()` throws. Both callers treat a failed enumeration as a
 * failure rather than as an empty device, and for the migration that distinction is
 * load-bearing: an empty answer believed would flip a device onto an engine holding
 * none of its data.
 */
export async function listPouchIndexedDbNamesAsync(
  indexedDb: Pick<IdbForDeletion, 'databases'>,
  options: ListPouchIndexedDbNamesOptions,
): Promise<string[]> {
  const databases = await indexedDb.databases();
  return databases
    .map(database => database.name)
    .filter((name): name is string => name?.startsWith(LEGACY_DB_NAME_PREFIX) ?? false)
    .filter(name => !options.excludeViewIndexes || !name.includes(VIEW_INDEX_NAME_MARKER));
}

/**
 * localStorage key that records the cleanup as finished.
 *
 * The `_v2` name is what re-arms the cleanup on a device that already ran the ORIGINAL one,
 * which marked every device finished whether or not its deletions worked (#4834). Reading a
 * NEW key gives each device one more run.
 *
 * This key gates a DELETE-ONLY pass and has never imported or copied anything, and on iOS
 * native the app's PouchDB data is in SQLite and nowhere else — which together are why
 * re-running the deletion is safe. The evidence for both is in
 * `design-docs/local-storage-and-auto-plan-cache.md`, "Re-arming the cleanup (#4834)". Read it
 * before changing either key name.
 */
export const SQLITE_MIGRATION_DONE_KEY = 'sqlite_migration_idb_cleanup_done_v2';

/**
 * localStorage key counting how many boots have tried this cleanup.
 *
 * Versioned in step with the done key above, and that is load-bearing rather than tidiness: a
 * device that already spent all `MAX_CLEANUP_ATTEMPTS` under #4771 would otherwise inherit a
 * spent budget, give up after a single try, and leave the re-arm worthless on precisely the
 * devices it exists for. Move one of these two names and the other moves with it.
 *
 * Deliberately NOT in the preserved list in `local-storage-hygiene.ts`, so signing out wipes
 * it. That is what we want: sign-out only drops the done key once the SQLite databases are
 * really gone, and when it does, the next account should start with a fresh budget rather than
 * inherit a spent one.
 */
export const SQLITE_MIGRATION_ATTEMPTS_KEY = 'sqlite_migration_idb_cleanup_attempts_v2';

/**
 * The key names this cleanup used before the re-arm, removed on the boot that re-arms so they
 * do not sit on every device for ever. Nothing reads them any more.
 */
export const SUPERSEDED_CLEANUP_KEYS: readonly string[] = [
  'sqlite_migration_complete',
  'sqlite_migration_idb_cleanup_attempts',
];

/** How many boots may try the cleanup before it gives up and stops retrying. */
export const MAX_CLEANUP_ATTEMPTS = 3;

/**
 * How long to wait for all the deletions to answer before giving up on them.
 *
 * Sized generously rather than tightly, because nothing waits on this: both call sites fire
 * the cleanup and forget it, so the wait costs a boot nothing.
 */
export const CLEANUP_DELETION_TIMEOUT_MS = 5_000;

/** Problem name for a cleanup that threw before it could finish issuing its deletions. */
export const CLEANUP_FAILED_PROBLEM = 'SqliteMigrationIdbCleanupFailed';

/** Problem name for one or more deletions that did not confirm. */
export const DELETION_FAILED_PROBLEM = 'SqliteMigrationIdbDeletionFailed';

/** Problem name for the last attempt, after which the cleanup stops retrying. */
export const CLEANUP_GAVE_UP_PROBLEM = 'SqliteMigrationIdbCleanupGaveUp';

/** The part of LoggingService this cleanup needs, named so the module stays testable. */
export interface CleanupProblemReporter {
  trackSilentProblem(name: string, context: { [key: string]: any }): void;
}

export interface LegacyIdbCleanupDeps {
  indexedDb: IdbForDeletion;
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  reporter: CleanupProblemReporter;
  /**
   * Which platform this device is, put into every report this cleanup files. (#5270 step 6b)
   *
   * The problem names below carry no platform of their own, so an Android deletion failure
   * could not be told from an iOS one while a migration ramp was being watched — and both
   * platforms run this same pass. Passed IN rather than read here, because this module must
   * stay loadable with no `environment-helpers` import: that import breaks the two node
   * suites that load this file.
   *
   * Absent simply leaves the field out, which is what a test that does not care gets.
   */
  platform?: string;
  /** Overridable so a test driving the never-answers case does not wait the full bound. */
  deletionTimeoutMs?: number;
}

/** Why one attempt did not finish the job, and what to say about it. */
interface CleanupFailure {
  problem: string;
  context: { [key: string]: any };
  /** the databases still on the device, or '' when the enumeration itself failed */
  failedDbNames: string;
}

/** Every console line this cleanup writes carries it, so a reader knows which pass wrote them. */
const CLEANUP_LOG_PREFIX = 'SQLite migration: ';

/** Run the deletions once, and say what stopped it finishing — or nothing, when it finished. */
async function attemptCleanupAsync(
  deps: LegacyIdbCleanupDeps,
  timeoutMs: number,
): Promise<CleanupFailure | undefined> {
  let results: IdbDeletionResult[];
  try {
    // `excludeViewIndexes: false` keeps this pass's behaviour byte-for-byte what it
    // was: a view index left behind by the old engine is still wasted storage, and
    // deleting it costs nothing because PouchDB rebuilds one on demand.
    const legacyNames = await listPouchIndexedDbNamesAsync(deps.indexedDb, {
      excludeViewIndexes: false,
    });
    // no `shouldWaitFor`: this cleanup waits on every database it deletes. Nothing waits on the
    // cleanup itself, so the bound costs a boot nothing, and a database that stays silent is a
    // result this pass genuinely wants — it is what stops the marker being written.
    results = await deleteIdbDatabasesAsync(deps.indexedDb, legacyNames, {
      timeoutMs,
      logPrefix: CLEANUP_LOG_PREFIX,
    });
  } catch (e) {
    // trackSilentProblem, NOT trackSilentInfo: nothing has been repaired by the time this
    // runs. The retry only comes on a later boot, and a device where it fails every time
    // never gets one that works, so the condition is still open here. trackAbnormalException
    // is wrong in the other direction — it shows the user a toast, and this is background
    // housekeeping nobody should be interrupted for.
    return { problem: CLEANUP_FAILED_PROBLEM, context: errorReportFields(e), failedDbNames: '' };
  }

  // Anything the browser did not confirm as gone is still on the device, so `Deleted` is the only
  // outcome that counts as done. In particular BLOCKED counts as a survivor here, because this pass
  // can come back on a later boot. The sign-out wipe reads that same outcome the OTHER way;
  // `idb-database-deletion.ts` says why the two callers are allowed to disagree.
  const survivors = results.filter(result => result.outcome !== IdbDeletionOutcome.Deleted);
  if (survivors.length === 0) return undefined;

  const failedDbNames = survivors.map(survivor => survivor.name).join(',');
  return {
    problem: DELETION_FAILED_PROBLEM,
    context: {
      failed_count: survivors.length,
      total_count: results.length,
      // name the databases that stayed behind, so the leftover's size is visible in Sentry
      failed_db_names: failedDbNames,
      // and say WHICH way each one went: refused, blocked and never-answered need different
      // follow-ups, and the names alone cannot tell them apart
      failed_outcomes: survivors.map(survivor => `${survivor.name}=${survivor.outcome}`).join(','),
      ...errorReportFields(survivors.find(survivor => survivor.error !== undefined)?.error),
    },
    failedDbNames,
  };
}

/**
 * The platform field to merge into a report, or nothing when the caller did not say.
 *
 * A helper rather than a spread at each site so the field NAME is written once: it is what
 * a ramp check filters on, and two spellings would split one Sentry query into two.
 */
function platformField(deps: LegacyIdbCleanupDeps): { platform?: string } {
  return deps.platform === undefined ? {} : { platform: deps.platform };
}

/** Read the attempt counter, treating anything unreadable as "no attempts yet". */
function readAttemptCount(storage: Pick<Storage, 'getItem'>): number {
  const stored = Number.parseInt(storage.getItem(SQLITE_MIGRATION_ATTEMPTS_KEY) ?? '', 10);
  return Number.isFinite(stored) && stored > 0 ? stored : 0;
}

/**
 * Delete the IndexedDB databases the pre-SQLite storage engine left behind on this device.
 *
 * Records the cleanup as finished only when every deletion is confirmed, so a boot where one
 * fails tries again on a later boot. Never throws: both call sites fire it and forget it, so
 * a throw would surface as an unhandled rejection in the middle of a boot.
 */
export async function cleanUpLegacyIndexedDbAsync(deps: LegacyIdbCleanupDeps): Promise<void> {
  const { storage, reporter } = deps;
  if (storage.getItem(SQLITE_MIGRATION_DONE_KEY)) return;

  // drop the names this cleanup used before the re-arm. Done HERE rather than beside the
  // early return above, so a device that is already finished under the new key pays nothing
  // on every later boot — by then the old names are long gone anyway.
  for (const supersededKey of SUPERSEDED_CLEANUP_KEYS) {
    storage.removeItem(supersededKey);
  }

  // count this boot BEFORE doing the work, so an attempt that dies half way through still
  // spends part of the budget rather than letting a wedging device try for ever
  const attempts = readAttemptCount(storage) + 1;
  storage.setItem(SQLITE_MIGRATION_ATTEMPTS_KEY, String(attempts));

  const failure = await attemptCleanupAsync(
    deps, deps.deletionTimeoutMs ?? CLEANUP_DELETION_TIMEOUT_MS);

  if (!failure) {
    storage.setItem(SQLITE_MIGRATION_DONE_KEY, 'true');
    console.log('SQLite migration: cleaned up old IDB databases');
    return;
  }

  reporter.trackSilentProblem(failure.problem, {
    ...failure.context,
    attempt: attempts,
    max_attempts: MAX_CLEANUP_ATTEMPTS,
    ...platformField(deps),
  });

  if (attempts >= MAX_CLEANUP_ATTEMPTS) {
    // stop trying. Writing the marker here is what makes that stick — see the bounding note
    // at the top of this file for why giving up is the better of the two bad options.
    storage.setItem(SQLITE_MIGRATION_DONE_KEY, 'true');
    reporter.trackSilentProblem(CLEANUP_GAVE_UP_PROBLEM, {
      attempts,
      failed_db_names: failure.failedDbNames,
      last_problem: failure.problem,
      ...platformField(deps),
    });
  }
}
